import { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import { faArrowDown, faArrowUp, faPencil, faToggleOff, faToggleOn } from '@fortawesome/free-solid-svg-icons';

import { Button } from '../../components/shared/FormElements';
import { ProcessRoleType, ProcessType } from '../../types';
import { useAppDispatch } from '../../hooks';
import { setPopAlert } from '../../slices/miscSlice';
import { useAppContext } from '../../Context';
import IconAwesome from '../../components/shared/IconAwesome';
import '../../styles/DataTable.css';
import { constants } from '../../constants';
import { usePermissions } from '../../services';

type ProcessFormState = {
  process_name: string;
  process_description: string;
  process_icon: string;
  sort_order: number | '';
  process_slug_permission: string;
  is_deprecated: boolean;
};

const emptyFormState: ProcessFormState = {
  process_name: '',
  process_description: '',
  process_icon: '',
  sort_order: '',
  process_slug_permission: '',
  is_deprecated: false,
};

const ViewProcessesSettings: React.FC<{ active: boolean }> = ({ active }) => {
  const [processes, setProcesses] = useState<Array<ProcessType>>([]);
  const [addNewForm, setAddNewForm] = useState(false);
  const [editForm, setEditForm] = useState(false);
  const [newProcess, setNewProcess] = useState<ProcessFormState>({ ...emptyFormState });
  const [editProcess, setEditProcess] = useState<ProcessType | null>(null);
  const [iconFilter, setIconFilter] = useState('');
  const [processPermissions, setProcessPermissions] = useState<Array<string>>([]);
  const [processRoles, setProcessRoles] = useState<Array<ProcessRoleType>>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [permissionOptions, setPermissionOptions] = useState<Array<{ permission: string; label: string }>>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const { apiCall } = useAppContext();
  const { canView_process, canAdd_process, canChange_process } = usePermissions([
    'view_process',
    'add_process',
    'change_process',
  ]);

  const getProcesses = async () => {
    const response = await apiCall('GET', 'processes/admin/', null, '', 'Error al obtener los procesos');
    if (response) {
      const sorted = [...response].sort((a, b) => a.sort_order - b.sort_order);
      setProcesses(sorted);
    }
  };

  useEffect(() => {
    if (!active || hasLoaded) return;
    getProcesses();
    setHasLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, hasLoaded]);

  useEffect(() => {
    if (!active || permissionsLoaded) return;
    const loadPermissions = async () => {
      const response = await apiCall('GET', 'permissions/catalog/', null, '', 'Error al obtener los permisos');
      if (response?.process_permissions) {
        const options = response.process_permissions
          .map((permission: { permission?: string; label?: string }) => {
            const code = permission.permission || '';
            if (!code) return null;
            const label = permission.label || constants.permission_labels[code] || code;
            return { permission: code, label };
          })
          .filter(Boolean) as Array<{ permission: string; label: string }>;
        setPermissionOptions(options.sort((a, b) => a.label.localeCompare(b.label)));
        setPermissionsLoaded(true);
        return;
      }

      const fallback = Object.values(constants.process_permissions)
        .map((permission) => ({
          permission,
          label: constants.permission_labels[permission] || permission,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setPermissionOptions(fallback);
      setPermissionsLoaded(true);
    };

    loadPermissions();
  }, [active, apiCall, permissionsLoaded]);

  const iconOptions = useMemo(() => {
    return Object.entries(solidIcons)
      .filter(([key, value]) => {
        if (!key.startsWith('fa')) return false;
        if (!value || typeof value !== 'object') return false;
        return 'iconName' in value && 'prefix' in value;
      })
      .map(([key, value]) => ({ key, icon: value as IconDefinition }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, []);

  const permissionGroups = useMemo(() => {
    const groups: Record<string, Array<{ permission: string; label: string }>> = {};
    const groupLabelFor = (permission: string, label: string) => {
      const cleaned = label.replace(/^(Ver|Crear|Editar|Eliminar)\s+/i, '').trim();
      if (cleaned && cleaned !== label) {
        return cleaned;
      }
      const parts = permission.split('_').slice(1).join(' ');
      return parts || permission;
    };
    permissionOptions.forEach((option) => {
      const groupLabel = groupLabelFor(option.permission, option.label);
      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(option);
    });
    return Object.entries(groups)
      .map(([groupLabel, items]) => ({
        groupLabel,
        items: items.sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
  }, [permissionOptions]);

  const handleToggleProcessPermission = async (permission: string) => {
    if (!canChange_process) return;
    if (!editProcess) return;
    const nextPermissions = processPermissions.includes(permission)
      ? processPermissions.filter((item) => item !== permission)
      : [...processPermissions, permission];
    const previousPermissions = processPermissions;
    setProcessPermissions(nextPermissions);
    const response = await apiCall(
      'PUT',
      `processes/admin/${editProcess.id}/permissions/`,
      { permissions: nextPermissions },
      'Permisos actualizados',
      'Error al actualizar los permisos',
    );
    if (!response?.permissions) {
      setProcessPermissions(previousPermissions);
    }
  };

  const handleToggleProcessRole = async (role: ProcessRoleType) => {
    if (!canChange_process) return;
    if (!editProcess) return;
    const roleProcessId = typeof role.process === 'object' ? role.process?.id : role.process;
    const shouldAssign = roleProcessId !== editProcess.id;
    const previousRoles = processRoles;
    setProcessRoles((prev) =>
      prev.map((item) =>
        item.id === role.id ? { ...item, process: shouldAssign ? editProcess.id : null } : item,
      ),
    );
    const response = await apiCall(
      'PUT',
      `process_roles/${role.id}/assign_process/`,
      { process_id: shouldAssign ? editProcess.id : null },
      'Rol actualizado',
      'Error al actualizar el rol',
    );
    if (!response?.id) {
      setProcessRoles(previousRoles);
    }
  };

  const renderProcessPermissions = () => (
    <FormControl component="fieldset" variant="standard">
      <FormGroup>
        <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '0.5rem' }}>
          {permissionGroups.map((group) => (
            <div key={group.groupLabel} style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{group.groupLabel}</div>
              {group.items.map((option) => (
                <FormControlLabel
                  key={option.permission}
                  control={
                    <Switch
                      checked={processPermissions.includes(option.permission)}
                      onChange={() => handleToggleProcessPermission(option.permission)}
                      disabled={!canChange_process}
                    />
                  }
                  label={option.label}
                />
              ))}
            </div>
          ))}
        </div>
      </FormGroup>
    </FormControl>
  );

  const renderProcessRoles = () => (
    <>
      {processRoles.length === 0 && <p>No hay roles de proceso asociados</p>}
      {processRoles.map((role) => {
        const roleProcessId = typeof role.process === 'object' ? role.process?.id : role.process;
        const isAssigned = roleProcessId === editProcess?.id;
        return (
          <FormControlLabel
            key={role.id}
            control={<Switch checked={isAssigned} onChange={() => handleToggleProcessRole(role)} disabled={!canChange_process} />}
            label={role.name}
          />
        );
      })}
    </>
  );

  const filteredIcons = useMemo(() => {
    const filter = iconFilter.trim().toLowerCase();
    if (!filter) return iconOptions;
    return iconOptions.filter((option) => option.key.toLowerCase().includes(filter));
  }, [iconFilter, iconOptions]);

  const handleSaveProcess = async () => {
    if (newProcess.process_name.trim() === '') {
      dispatch(setPopAlert({ alertType: 'error', message: 'El nombre del proceso es requerido', show: true }));
      return;
    }
    if (newProcess.process_icon.trim() === '') {
      dispatch(setPopAlert({ alertType: 'error', message: 'El icono del proceso es requerido', show: true }));
      return;
    }
    if (newProcess.sort_order === '' || Number.isNaN(Number(newProcess.sort_order))) {
      dispatch(setPopAlert({ alertType: 'error', message: 'El orden es requerido', show: true }));
      return;
    }
    const data = {
      process_name: newProcess.process_name,
      process_description: newProcess.process_description,
      process_icon: newProcess.process_icon,
      sort_order: Number(newProcess.sort_order),
      process_slug_permission: newProcess.process_slug_permission || null,
      is_deprecated: newProcess.is_deprecated,
    };
    const response = await apiCall(
      'POST',
      'processes/admin/',
      data,
      'Proceso creado exitosamente',
      'Error al crear el proceso',
    );
    if (response?.id) {
      const updated = [...processes, response].sort((a, b) => a.sort_order - b.sort_order);
      setProcesses(updated);
      setAddNewForm(false);
      setNewProcess({ ...emptyFormState });
      setIconFilter('');
    }
  };

  const handleEditProcess = async (process: ProcessType) => {
    setEditForm(true);
    setAddNewForm(false);
    setEditProcess(process);
    setNewProcess({
      process_name: process.process_name,
      process_description: process.process_description,
      process_icon: process.process_icon,
      sort_order: process.sort_order,
      process_slug_permission: process.process_slug_permission || '',
      is_deprecated: Boolean(process.is_deprecated),
    });
    const response = await apiCall(
      'GET',
      `processes/admin/${process.id}/permissions/`,
      null,
      '',
      'Error al obtener los permisos del proceso',
    );
    if (response?.permissions) {
      setProcessPermissions(response.permissions);
    } else {
      setProcessPermissions([]);
    }
    const rolesResponse = await apiCall('GET', 'process_roles/', null, '', 'Error al obtener los roles de proceso');
    if (rolesResponse) {
      setProcessRoles(rolesResponse);
    } else {
      setProcessRoles([]);
    }
  };

  const handleUpdateProcess = async () => {
    if (!editProcess) return;
    if (newProcess.process_name.trim() === '') {
      dispatch(setPopAlert({ alertType: 'error', message: 'El nombre del proceso es requerido', show: true }));
      return;
    }
    if (newProcess.process_icon.trim() === '') {
      dispatch(setPopAlert({ alertType: 'error', message: 'El icono del proceso es requerido', show: true }));
      return;
    }
    if (newProcess.sort_order === '' || Number.isNaN(Number(newProcess.sort_order))) {
      dispatch(setPopAlert({ alertType: 'error', message: 'El orden es requerido', show: true }));
      return;
    }
    const data = {
      process_name: newProcess.process_name,
      process_description: newProcess.process_description,
      process_icon: newProcess.process_icon,
      sort_order: Number(newProcess.sort_order),
      process_slug_permission: newProcess.process_slug_permission || null,
      is_deprecated: newProcess.is_deprecated,
    };
    const response = await apiCall(
      'PUT',
      `processes/admin/${editProcess.id}/`,
      data,
      'Proceso actualizado exitosamente',
      'Error al actualizar el proceso',
    );
    if (response?.id) {
      const updated = processes
        .map((item) => (item.id === response.id ? response : item))
        .sort((a, b) => a.sort_order - b.sort_order);
      setProcesses(updated);
      setEditForm(false);
      setEditProcess(null);
      setNewProcess({ ...emptyFormState });
      setIconFilter('');
      setProcessPermissions([]);
      setProcessRoles([]);
    }
  };

  const handleToggleDeprecated = async (process: ProcessType, isDeprecated: boolean) => {
    const response = await apiCall(
      'PUT',
      `processes/admin/${process.id}/`,
      { is_deprecated: isDeprecated },
      isDeprecated ? 'Proceso descontinuado exitosamente' : 'Proceso restablecido exitosamente',
      isDeprecated ? 'Error al descontinuar el proceso' : 'Error al restablecer el proceso',
    );
    if (response?.id) {
      const updated = processes
        .map((item) => (item.id === response.id ? response : item))
        .sort((a, b) => a.sort_order - b.sort_order);
      setProcesses(updated);
    }
  };

  const handleMoveProcess = async (process: ProcessType, direction: 'up' | 'down') => {
    const response = await apiCall(
      'POST',
      `processes/admin/${process.id}/move/`,
      { direction },
      'Orden actualizado',
      'Error al actualizar el orden',
    );
    if (response) {
      await getProcesses();
    }
  };

  const renderIconPicker = (selectedIcon: string, onSelect: (iconKey: string) => void) => (
    <div style={{ marginBottom: '1.5rem' }}>
      <input
        type="text"
        placeholder="Buscar icono (ej: faPaperclip)"
        value={iconFilter}
        onChange={(event) => setIconFilter(event.target.value)}
        className="settings-input form-control"
      />
      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: '6px',
          padding: '0.5rem',
          maxHeight: '260px',
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
          gap: '0.5rem',
        }}
      >
        {[...filteredIcons]
          .sort((a, b) => {
            if (a.key === selectedIcon) return -1;
            if (b.key === selectedIcon) return 1;
            return a.key.localeCompare(b.key);
          })
          .map((option) => (
            <button
              type="button"
              key={option.key}
              onClick={() => onSelect(option.key)}
              style={{
                cursor: 'pointer',
                border: option.key === selectedIcon ? '2px solid #1f6feb' : '1px solid #ccc',
                borderRadius: '6px',
                padding: '0.35rem',
                background: option.key === selectedIcon ? '#eef4ff' : '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <FontAwesomeIcon icon={option.icon} />
            </button>
          ))}
      </div>
    </div>
  );

  const renderForm = (isEdit: boolean) => (
    <div>
      <h3>{isEdit ? 'Editar Proceso' : 'Agregar Nuevo Proceso'}</h3>
      <form>
        <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Nombre del proceso</div>
        <input
          type="text"
          placeholder="Nombre del proceso"
          value={newProcess.process_name}
          onChange={(event) => setNewProcess({ ...newProcess, process_name: event.target.value })}
          className="settings-input form-control"
        />
        <div style={{ marginBottom: '0.5rem', fontWeight: 600, marginTop: '1rem' }}>Descripción del proceso</div>
        <textarea
          placeholder="Descripción del proceso"
          value={newProcess.process_description}
          onChange={(event) => setNewProcess({ ...newProcess, process_description: event.target.value })}
          className="settings-input form-control"
          rows={3}
        />
        <div style={{ marginBottom: '0.5rem', fontWeight: 600, marginTop: '1rem' }}>Orden</div>
        <input
          type="number"
          placeholder="Orden"
          value={newProcess.sort_order}
          className="settings-input form-control"
          disabled
        />
        <div style={{ marginBottom: '0.5rem', fontWeight: 600, marginTop: '1rem' }}>Slug permiso</div>
        <input
          type="text"
          placeholder="Slug permiso (ej: corte)"
          value={newProcess.process_slug_permission}
          onChange={(event) => setNewProcess({ ...newProcess, process_slug_permission: event.target.value })}
          className="settings-input form-control"
        />
        <FormControlLabel
          control={
            <Switch
              checked={newProcess.is_deprecated}
              onChange={(event) => setNewProcess({ ...newProcess, is_deprecated: event.target.checked })}
            />
          }
          label="Proceso obsoleto"
        />
        {isEdit && (
          <div style={{ marginBottom: '1.5rem', marginTop: '1.25rem' }}>
            <h4>Permisos de Proceso</h4>
            {renderProcessPermissions()}
          </div>
        )}
        {isEdit && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h4>Roles de Proceso</h4>
            {renderProcessRoles()}
          </div>
        )}
        <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Selecciona un icono</div>
        {renderIconPicker(newProcess.process_icon, (iconKey) =>
          setNewProcess({
            ...newProcess,
            process_icon: iconKey,
          }),
        )}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              onClick={() => {
                if (isEdit) {
                  setEditForm(false);
                  setEditProcess(null);
                } else {
                  setAddNewForm(false);
                }
                setNewProcess({ ...emptyFormState });
                setIconFilter('');
                setProcessPermissions([]);
                setProcessRoles([]);
              }}
            >
              Cancelar
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button onClick={isEdit ? handleUpdateProcess : handleSaveProcess}>
              {isEdit ? 'Guardar Cambios' : 'Guardar'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );

  if (!canView_process) {
    return null;
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className="text-right">
          <h2>Procesos</h2>
        </Grid>
        <Grid item xs={12} sm={2} className="text-right">
          {canAdd_process && (
            <Button
              onClick={() => {
                setAddNewForm(true);
                setEditForm(false);
                setEditProcess(null);
                setNewProcess({ ...emptyFormState });
              }}
            >
              Agregar
            </Button>
          )}
        </Grid>
      </Grid>
      {addNewForm && canAdd_process && renderForm(false)}
      {editForm && canChange_process && renderForm(true)}
      {processes.length === 0 && <p>No hay procesos registrados</p>}
      <Grid container spacing={2}>
        <div className="table-responsive">
          <TableContainer className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>ICONO</b>
                  </TableCell>
                  <TableCell>
                    <b>NOMBRE</b>
                  </TableCell>
                  <TableCell>
                    <b>ORDEN</b>
                  </TableCell>
                  <TableCell>
                    <b>OBSOLETO</b>
                  </TableCell>
                  <TableCell width={120}>
                    <b>ACCIONES</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processes.map((process, index) => {
                  const isFirst = index === 0;
                  const isLast = index === processes.length - 1;
                  return (
                  <TableRow
                    key={process.id}
                    style={process.is_deprecated ? { backgroundColor: '#f8d7da' } : undefined}
                  >
                    <TableCell>{process.process_icon ? <IconAwesome processIcon={process.process_icon} /> : '-'}</TableCell>
                    <TableCell>{process.process_name}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{process.sort_order}</span>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          {canChange_process && (
                            <Button onClick={() => handleMoveProcess(process, 'up')} disabled={isFirst}>
                              <FontAwesomeIcon icon={faArrowUp} />
                            </Button>
                          )}
                          {canChange_process && (
                            <Button onClick={() => handleMoveProcess(process, 'down')} disabled={isLast}>
                              <FontAwesomeIcon icon={faArrowDown} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{process.is_deprecated ? 'Si' : 'No'}</TableCell>
                    <TableCell>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          {canChange_process && (
                            <Button onClick={() => handleEditProcess(process)}>
                              <FontAwesomeIcon icon={faPencil} />
                            </Button>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          {canChange_process && (
                            process.is_deprecated ? (
                              <Button
                                onClick={() => handleToggleDeprecated(process, false)}
                                title="Restablecer"
                              >
                                <FontAwesomeIcon icon={faToggleOn} style={{ color: '#74c69d' }} />
                              </Button>
                            ) : (
                              <Button
                                onClick={() => handleToggleDeprecated(process, true)}
                                title="Descontinuar"
                              >
                                <FontAwesomeIcon icon={faToggleOff} style={{ color: '#f1a7b5' }} />
                              </Button>
                            )
                          )}
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Grid>
    </>
  );
};

export default ViewProcessesSettings;
